import type { ReactNode } from 'react';
import React, { useCallback, useMemo } from 'react';
import { MultipleChoiceFilter, FilterGroup } from '@lt/components';
import type { IFilter } from '../types';

interface makeItemsFilterParam {
  customFilters: string[];
  FILTER_NAMES: Record<string, string>;
  FILTER_DESCRIPTIONS?: Record<string, string>;
  description?: string;
}

const makeItems = (
  data,
  {
    customFilters,
    FILTER_NAMES,
    description,
    FILTER_DESCRIPTIONS,
  }: makeItemsFilterParam,
) =>
  customFilters
    .map((filterName) => {
      const filterData = data[filterName];

      if (!filterData) {
        return null;
      }

      const [item] = filterData;

      return {
        ...item,
        path: filterName,
        name: FILTER_NAMES[filterName],
        description: FILTER_DESCRIPTIONS
          ? FILTER_DESCRIPTIONS[filterName]
          : description,
      };
    })
    .filter(Boolean);

interface ICustomBooleanFilterGroup extends IFilter {
  Filter: {
    customFilters: string[];
    FILTER_NAMES: Record<string, string>;
    FILTER_DESCRIPTIONS?: Record<string, string>;
    description?: string;
    filterLabel: ReactNode;
  };
}

/**
 * Фильтр, составленный на фронте из кучи булевых фильтров
 * e.g: filter_aquapark, filter_thermal_fun, filter_pool
 */
const CustomBooleanFilterGroup = (
  props: ICustomBooleanFilterGroup,
): JSX.Element => {
  const {
    data = {},
    onChange,
    onClear,
    Filter,
    isMobile = false,
    className = '',
    currencyCode,
  } = props;

  const items = useMemo(() => makeItems(data, Filter), [data, Filter]);

  const hasActiveFilters = items.some((item) => item.selected);

  const handleChange = useCallback(
    ({ path, id, selected }) => onChange({ path, diff: { id, selected } }),
    [onChange],
  );

  const handleClear = useCallback(
    () => items.forEach(({ path, selected }) => selected && onClear(path)),
    [items, onClear],
  );

  return (
    <FilterGroup
      className={className}
      label={Filter.filterLabel}
      onClear={handleClear}
      showClearBtn={hasActiveFilters}
      isMobile={isMobile}
    >
      <MultipleChoiceFilter
        items={items}
        onChange={handleChange}
        isMobile={isMobile}
        currencyCode={currencyCode}
      />
    </FilterGroup>
  );
};

export default CustomBooleanFilterGroup;
